import { supabase } from '../../../core/lib/supabase';
import type { WorkPillExecution } from '../../../types/navigationTypes';
import type { TemplateBlock } from '../../blocks/types/blockTypes';
import {
  uploadTemplateToExecutionStorage,
  uploadUserFileToExecutionTemplateStorage,
  getExecutionTemplateDisplayNameFromFile,
  BLANK_TEMPLATE_ID,
  BLANK_EXCEL_TEMPLATE_ID,
} from '../services/uploadTemplateToExecutionStorage';
import { processTemplateViaOrganizationBackend } from '../services/organizationBackendTemplateService';
import type { SelectedTemplateWithConfig } from './wizardTypes';

const EXECUTION_TEMPLATES_BUCKET = 'execution-templates';

export async function addExecutionTemplate(
  executionId: string,
  templateId: string,
  options?: { name?: string | null }
): Promise<{ executionTemplateId: string }> {
  const row: {
    workpill_execution_id: string;
    template_id: string;
    name?: string | null;
  } = { workpill_execution_id: executionId, template_id: templateId };
  if (options?.name != null && String(options.name).trim() !== '') {
    row.name = String(options.name).trim();
  }

  const { data, error } = await supabase.from('workpill_execution_templates').insert(row).select('id').single();

  if (error) throw new Error(error.message || 'add execution template failed');
  if (!data?.id) throw new Error('Failed to get execution template id');

  return { executionTemplateId: data.id };
}

export async function upsertExecutionTemplateVariables(
  executionTemplateId: string,
  templateId: string,
  values: Record<string, string>
): Promise<void> {
  const fieldNames = Object.keys(values);
  if (fieldNames.length === 0) return;

  const { data: existingFields, error: fieldsError } = await supabase
    .from('template_fields')
    .select('id, name')
    .eq('template_id', templateId)
    .in('name', fieldNames);

  if (fieldsError) throw new Error(fieldsError.message || 'load template fields');

  const fieldIdByName = new Map<string, string>(
    (existingFields || []).map((f: { id: string; name: string }) => [f.name, f.id])
  );

  const missingNames = fieldNames.filter((name) => !fieldIdByName.has(name));
  if (missingNames.length > 0) {
    const newFields = missingNames.map((name) => ({
      template_id: templateId,
      name,
      type: 'text',
    }));

    const { data: inserted, error: insertError } = await supabase
      .from('template_fields')
      .insert(newFields)
      .select('id, name');

    if (insertError) throw new Error(insertError.message || 'create template fields');

    (inserted || []).forEach((f: { id: string; name: string }) => {
      fieldIdByName.set(f.name, f.id);
    });
  }

  const rows = fieldNames
    .map((name) => {
      const template_field_id = fieldIdByName.get(name);
      if (!template_field_id) return null;
      return {
        execution_template_id: executionTemplateId,
        template_field_id,
        value: values[name] ?? null,
      };
    })
    .filter((r): r is NonNullable<typeof r> => r !== null);

  if (rows.length === 0) return;

  const { error } = await supabase
    .from('execution_template_variables')
    .upsert(rows, { onConflict: 'execution_template_id,template_field_id' });

  if (error) throw new Error(error.message || 'save execution template variables');
}

function generateBlockOrder(
  blocks: TemplateBlock[],
  slotCount: number
): { block_id: string; order: string }[] {
  if (blocks.length === 0) return [];

  if (slotCount === 1) {
    return blocks.map((b, idx) => ({
      block_id: b.id,
      order: `1.${idx + 1}`,
    }));
  }

  return blocks.map((b, idx) => {
    if (idx < slotCount) {
      return {
        block_id: b.id,
        order: (idx + 1).toString(),
      };
    }
    const extraIndex = idx - slotCount + 1;
    return {
      block_id: b.id,
      order: `${slotCount}.${extraIndex + 1}`,
    };
  });
}

export { generateBlockOrder };

/**
 * Best-effort cleanup after a failed create (matches web `deleteProject` storage + execution KB rows).
 */
export async function deleteProjectForRollback(projectId: string): Promise<void> {
  const { data: executionTemplates, error: fetchTemplatesError } = await supabase
    .from('workpill_execution_templates')
    .select('id')
    .eq('workpill_execution_id', projectId);

  if (fetchTemplatesError) {
    console.warn('deleteProjectForRollback: fetch templates', fetchTemplatesError.message);
  }

  const executionTemplateIds = (executionTemplates || []).map((row: { id: string }) => row.id);

  const { data: executionKBs } = await supabase
    .from('workpill_execution_knowledge_bases')
    .select(
      `
      knowledge_base_id,
      knowledge_bases!workpill_execution_knowledge_bases_knowledge_base_id_fkey (
        id,
        is_global
      )
    `
    )
    .eq('workpill_execution_id', projectId);

  type KbJoin = { id: string; is_global: boolean | null } | null;
  const rows = (executionKBs ?? []) as {
    knowledge_base_id: string;
    knowledge_bases: KbJoin | KbJoin[];
  }[];

  const candidateKbIds = rows
    .flatMap((ekb) => {
      const raw = ekb.knowledge_bases;
      const meta = Array.isArray(raw) ? raw[0] : raw;
      if (meta && meta.is_global === false) {
        return [ekb.knowledge_base_id];
      }
      return [];
    })
    .filter(Boolean) as string[];

  const { error } = await supabase.from('workpill_executions').delete().eq('id', projectId);

  if (error) {
    console.warn('deleteProjectForRollback: delete execution', error.message);
    return;
  }

  for (const executionTemplateId of executionTemplateIds) {
    try {
      const { data: listData, error: listError } = await supabase.storage
        .from(EXECUTION_TEMPLATES_BUCKET)
        .list(executionTemplateId);

      if (listError) continue;
      const files = (listData || []).filter((f): f is typeof f & { name: string } => !!f.name);
      if (!files.length) continue;
      const pathsToRemove = files.map((f) => `${executionTemplateId}/${f.name}`);
      await supabase.storage.from(EXECUTION_TEMPLATES_BUCKET).remove(pathsToRemove);
    } catch {
      /* ignore */
    }
  }

  for (const kbId of candidateKbIds) {
    try {
      await supabase.from('knowledge_bases').delete().eq('id', kbId);
    } catch {
      /* ignore */
    }
  }
}

export interface CreateProjectFlowParams {
  userId: string;
  executionId: string;
  projectName: string;
  workpillId: string | null;
  templateConfigs: SelectedTemplateWithConfig[];
  knowledgeBaseIds: string[];
  uploadedTemplateFiles: File[];
}

function mapExecutionRow(execution: Record<string, unknown>): WorkPillExecution {
  const wpRaw = execution.workpills;
  const workpillData = Array.isArray(wpRaw) ? wpRaw[0] : wpRaw;
  const wp = workpillData as
    | { id: string; name: string; description?: string; created_at: string; created_by: string }
    | null
    | undefined;

  return {
    id: execution.id as string,
    workpill_id: (execution.workpill_id as string | null) ?? null,
    created_by: execution.created_by as string,
    state_id: execution.state_id as string,
    name: (execution.name as string) || 'Unnamed Execution',
    comments: execution.comments as string | undefined,
    last_update: execution.last_update as string,
    created_at: execution.created_at as string,
    workpill: wp
      ? {
          id: wp.id,
          name: wp.name,
          description: wp.description,
          created_at: wp.created_at,
          created_by: wp.created_by,
        }
      : undefined,
  };
}

export async function createProjectWithWizardResources(params: CreateProjectFlowParams): Promise<WorkPillExecution> {
  const {
    userId,
    executionId,
    projectName,
    workpillId,
    templateConfigs,
    knowledgeBaseIds,
    uploadedTemplateFiles,
  } = params;

  let createdProjectId: string | null = null;

  try {
    const { data: newProject, error: insertError } = await supabase
      .from('workpill_executions')
      .insert({
        id: executionId,
        created_by: userId,
        workpill_id: workpillId,
        name: projectName.trim(),
        last_update: new Date().toISOString(),
      })
      .select(
        `
        *,
        workpills (
          id,
          name,
          description,
          created_at,
          created_by
        )
      `
      )
      .single();

    if (insertError) throw new Error(insertError.message || 'create project failed');
    if (!newProject?.id) throw new Error('Project insert returned no row');

    createdProjectId = newProject.id;
    const projectId = newProject.id;

    const useConfigs = templateConfigs.length > 0;
    const templatesToInsert = useConfigs ? templateConfigs.map((c) => c.id) : [];

    let insertedTemplateLinks: { id: string; template_id: string }[] = [];

    if (templatesToInsert.length > 0) {
      const templateLinks = templatesToInsert.map((tId) => ({
        workpill_execution_id: projectId,
        template_id: tId,
      }));

      const { data, error: templateError } = await supabase
        .from('workpill_execution_templates')
        .insert(templateLinks)
        .select('id, template_id');

      if (templateError) throw new Error(templateError.message || 'link templates');
      insertedTemplateLinks = (data || []) as { id: string; template_id: string }[];
    }

    const configByTemplateId = useConfigs ? new Map(templateConfigs.map((c) => [c.id, c])) : null;

    for (const row of insertedTemplateLinks) {
      const { id: executionTemplateId, template_id: templateId } = row;
      const config = configByTemplateId?.get(templateId);

      const fieldValues: Record<string, string> = {};
      if (config) {
        Object.entries(config.fieldValues).forEach(([key, value]) => {
          const cleanKey = key.replace(/[{}]/g, '');
          fieldValues[cleanKey] = value;
        });
      }

      const blockOrder =
        config && config.blocks.length > 0 ? generateBlockOrder(config.blocks, config.slotCount) : undefined;

      try {
        await processTemplateViaOrganizationBackend(templateId, {
          execution_template_id: executionTemplateId,
          from_latest: true,
          field_values: fieldValues,
          block_order: blockOrder,
        });
        if (config && Object.keys(fieldValues).length > 0) {
          try {
            await upsertExecutionTemplateVariables(executionTemplateId, templateId, fieldValues);
          } catch {
            /* non-fatal, same as web */
          }
        }
      } catch (processError) {
        console.error('processTemplate failed, trying storage fallback:', processError);
        try {
          await uploadTemplateToExecutionStorage({
            executionTemplateId,
            templateId,
          });
        } catch (uploadErr) {
          throw new Error(
            uploadErr instanceof Error
              ? uploadErr.message
              : `Template ${templateId} could not be processed or uploaded.`
          );
        }
      }
    }

    for (const file of uploadedTemplateFiles) {
      const lower = file.name.toLowerCase();
      if (!lower.endsWith('.docx') && !lower.endsWith('.xlsx')) {
        console.warn('Skipping unsupported template file:', file.name);
        continue;
      }
      const catalogTemplateId = lower.endsWith('.xlsx') ? BLANK_EXCEL_TEMPLATE_ID : BLANK_TEMPLATE_ID;
      const displayName = getExecutionTemplateDisplayNameFromFile(file);
      const { executionTemplateId } = await addExecutionTemplate(projectId, catalogTemplateId, {
        name: displayName,
      });
      await uploadUserFileToExecutionTemplateStorage({ executionTemplateId, file });
    }

    if (knowledgeBaseIds.length > 0) {
      const kbLinks = knowledgeBaseIds.map((kbId) => ({
        workpill_execution_id: projectId,
        knowledge_base_id: kbId,
      }));

      const { error: kbError } = await supabase.from('workpill_execution_knowledge_bases').insert(kbLinks);

      if (kbError) throw new Error(kbError.message || 'link knowledge bases');
    }

    return mapExecutionRow(newProject as Record<string, unknown>);
  } catch (e) {
    if (createdProjectId) {
      try {
        await deleteProjectForRollback(createdProjectId);
      } catch (delErr) {
        console.error('Rollback failed:', delErr);
      }
    }
    throw e;
  }
}
